/**
 * Locations Page for Olympic Fit Wellness
 * Single location: Lewisville
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Calendar, ArrowRight } from 'lucide-react';

export default function LocationsPage() {
  const [selectedDay, setSelectedDay] = useState(null);

  const location = {
    name: 'Olympic Fit Wellness',
    address: '2601 E State Hwy 121',
    suite: 'Building 1 Suite 114',
    city: 'Lewisville',
    state: 'TX',
    zip: '75056',
    phone: '(469) 585-1023',
    email: 'info@olympicfitwellness.com',
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200&q=80',
    mapUrl: 'https://www.google.com/maps/search/?api=1&query=2601+E+State+Hwy+121+Building+1+Suite+114+Lewisville+TX+75056',
    hours: [
      { day: 'Monday', hours: '6:00 AM - 8:00 PM', isOpen: true },
      { day: 'Tuesday', hours: '6:00 AM - 8:00 PM', isOpen: true },
      { day: 'Wednesday', hours: '6:00 AM - 8:00 PM', isOpen: true },
      { day: 'Thursday', hours: '6:00 AM - 8:00 PM', isOpen: true },
      { day: 'Friday', hours: '6:00 AM - 3:00 PM', isOpen: true },
      { day: 'Saturday', hours: '8:00 AM - 3:00 PM', isOpen: true },
      { day: 'Sunday', hours: '1:00 PM - 4:00 PM', isOpen: true }
    ],
    amenities: [
      'State-of-the-Art Equipment',
      'Group Fitness Classes',
      'Personal Training',
      'Recovery & Wellness',
      'Sauna Therapy',
      'IV Wellness Services',
      'Nutrition Coaching',
      'Locker Rooms & Showers'
    ]
  };

  // Get current day
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  return (
    <div className="bg-white" data-testid="locations-page">
      {/* Hero */}
      <section className="relative bg-neutral-900 pt-32 pb-20">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <span className="badge-accent mb-6 inline-flex">Our Location</span>
          <h1 className="text-display-2 font-heading text-white mb-6">
            Visit Us in Lewisville
          </h1>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
            Experience Dallas's premier wellness destination with state-of-the-art facilities and expert guidance.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left - Image & Map */}
            <div>
              <div className="relative rounded-2xl overflow-hidden mb-6">
                <img 
                  src={location.image}
                  alt="Olympic Fit Wellness"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-6 left-6 text-white">
                  <h2 className="text-2xl font-heading font-bold mb-1">{location.name}</h2>
                  <p className="text-white/80">{location.city}, {location.state}</p>
                </div>
              </div>
              
              {/* Map Link */}
              <a 
                href={location.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-neutral-100 hover:bg-neutral-200 rounded-xl p-6 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-neutral-900 rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-neutral-900">Get Directions</p>
                      <p className="text-sm text-neutral-500">Open in Google Maps</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-neutral-400" />
                </div>
              </a>
            </div>

            {/* Right - Details */}
            <div>
              {/* Address & Contact */}
              <div className="bg-neutral-50 rounded-2xl p-8 mb-8">
                <h3 className="text-xl font-heading font-bold text-neutral-900 mb-6">Contact Information</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-neutral-500 mt-1" />
                    <div>
                      <p className="font-medium text-neutral-900">{location.address}</p>
                      <p className="text-neutral-600">{location.suite}</p>
                      <p className="text-neutral-600">{location.city}, {location.state} {location.zip}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Phone className="w-5 h-5 text-neutral-500" />
                    <a href={`tel:+1${location.phone.replace(/[^0-9]/g, '')}`} className="font-medium text-neutral-900 hover:text-[#3B82F6]">
                      {location.phone}
                    </a>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-neutral-50 rounded-2xl p-8 mb-8">
                <h3 className="text-xl font-heading font-bold text-neutral-900 mb-6 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Hours of Operation
                </h3>
                
                <div className="space-y-3">
                  {location.hours.map((item, idx) => (
                    <div 
                      key={idx}
                      className={`flex justify-between items-center py-2 px-3 rounded-lg ${
                        item.day === today ? 'bg-green-50 border border-green-200' : ''
                      }`}
                    >
                      <span className={`font-medium ${item.day === today ? 'text-green-700' : 'text-neutral-700'}`}>
                        {item.day}
                        {item.day === today && <span className="ml-2 text-xs text-green-600">(Today)</span>}
                      </span>
                      <span className={item.day === today ? 'text-green-700' : 'text-neutral-600'}>
                        {item.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Amenities */}
              <div className="bg-neutral-50 rounded-2xl p-8">
                <h3 className="text-xl font-heading font-bold text-neutral-900 mb-6">Amenities</h3>
                <div className="grid grid-cols-2 gap-3">
                  {location.amenities.map((amenity, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <span className="text-sm text-neutral-700">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-neutral-900">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl font-heading font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-neutral-400 mb-8 max-w-lg mx-auto">
            Visit us today or sign up online to become part of the Olympic Fit Wellness community.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/signup"
              className="bg-white text-neutral-900 font-semibold px-8 py-4 text-sm tracking-wider hover:bg-neutral-100 transition-colors"
            >
              JOIN TODAY
            </Link>
            <Link 
              to="/contact"
              className="border border-neutral-700 text-white font-semibold px-8 py-4 text-sm tracking-wider hover:border-neutral-500 transition-colors"
            >
              CONTACT US
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
